import { api } from '../api';

/**
 * Programar ensaios — as chamadas, com o caminho escrito.
 *
 * Mesma razão de `api/adensamento.ts`: o caminho é o contrato entre a tela
 * e a API, e contrato se lê. O teste `26` confere que toda rota `@Post`
 * tenha, em `web/src`, uma chamada com o caminho literal — é a prova de
 * que a interface alcança o que a API sabe criar.
 */

export interface EnsaioProgramado {
  id: string;
  amostra_id: string;
  metodo_id: string;
  status: string;
  amostra_codigo: string;
  identificacao_recebida: string | null;
  metodo_codigo: string;
  metodo_nome: string;
  metodo_classe: string | null;
}

export interface Programacao {
  amostraIds: string[];
  metodoIds: string[];
  osId?: string;
  dataPrevista?: string;
  repetir?: boolean;
  observacoes?: string;
}

export interface ResultadoProgramacao {
  criados: number;
  pulados: number;
  detalhe: {
    criados: { ensaio_id: string; amostra_codigo: string; metodo_codigo: string }[];
    pulados: { ensaio_id: string; amostra_codigo: string; metodo_codigo: string }[];
  };
  aviso: string | null;
}

/** O que já está programado, para a grade não oferecer o que já existe. */
export const verProgramacao = (p: { os?: string; amostras?: string[] }) => {
  const q = new URLSearchParams();
  if (p.os) q.set('os', p.os);
  if (p.amostras?.length) q.set('amostras', p.amostras.join(','));
  return api<EnsaioProgramado[]>(`/programacao?${q.toString()}`);
};

export const programar = (corpo: Programacao) =>
  api<ResultadoProgramacao>('/programacao', { metodo: 'POST', corpo });

/* ── A grade de uma OS (onda 35) ──────────────────────────────────────
 *
 * Uma viagem só. A tela antiga fazia três — amostras, métodos e o que já
 * está programado —, e três chamadas são três estados de carregamento e
 * três chances de a tela ficar meio pronta na frente de quem só quer
 * saber «quais ensaios as amostras desta OS vão fazer».
 */

export interface MetodoDaGrade {
  id: string;
  codigo: string;
  nome: string;
  grupo: string | null;
  laboratorio: string | null;
  classe: string | null;
  classe_nome: string | null;
  /* O escopo vem do banco (0083) — a tela não deduz de que bancada é o
     método, porque deduzir aqui seria a segunda fonte da mesma verdade. */
  escopo: string;
  escopo_nome: string;
  escopo_ordem: number;
  modelos_de_instrucao: number;
  /* `true` = a conta deste método ainda não foi reconhecida (0087). O
     ensaio pode ser programado e executado; o que ele não vai conseguir
     é ser APROVADO, e é melhor saber disso agora do que no fim. */
  sem_versao_calculo: boolean;
  /* 0220 (RN-218): o método está no escopo VIGENTE da OS? Só ele se
     oferece para programar; os outros vêm só para mostrar ensaio antigo. */
  na_os?: boolean;
}

export interface BateriaDaGrade {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  metodo_ids: string[];
}

export interface AmostraDaGrade {
  id: string;
  codigo: string;
  status: string;
  estado_custodia: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  tipo_amostra: string | null;
  /** Linha de amostra que AINDA NÃO CHEGOU (0161): `id` é `prev:<uuid>`. */
  prevista?: boolean;
  /** 0190: a coordenada prevista (de projeto ou estimada), quando há. */
  coordenada_prevista?: string | null;
}

export interface AmostraPrevistaDaGrade {
  id: string;
  identificacao_prevista: string | null;
  origem_prevista: string | null;
  observacoes: string | null;
  ordem: number;
  metodo_ids: string[];
  /** 0182: a instrução já escrita para cada método previsto (metodo_id → texto). */
  instrucoes?: Record<string, string>;
  /** 0188: o grupo da amostra antes de ela chegar, e a profundidade prevista. */
  tipo_amostra?: 'deformada' | 'indeformada' | 'cp_moldado' | null;
  profundidade_inicial?: string | null;
  profundidade_final?: string | null;
  /** 0190: a coordenada de onde se pretende coletar, em UTM. */
  origem_coordenada?: 'projeto' | 'estimada' | null;
  utm_fuso?: number | null;
  utm_e?: number | string | null;
  utm_n?: number | string | null;
}

/**
 * O escopo contratado da OS, por método (0149).
 *
 * «A quantidade é por método de ensaio» — Testesolo, 02/09/2026. É o
 * saldo do contrato na tela onde ele é gasto.
 */
export interface ContratadoDaGrade {
  metodo_id: string;
  metodo_codigo: string;
  /** 268: o nome e o pacote, para a faixa do escopo. */
  metodo_nome?: string | null;
  cps_por_ensaio?: number | null;
  quantidade_contratada: string;
  programados: number;
  cancelados: number;
  saldo: string;
  /* 0220 (D6): marcados em amostra por chegar */
  reservados?: number;
}

export interface GradeDaOs {
  programacao: SituacaoDaProgramacao;
  os: {
    id: string; numero: string; cliente: string | null;
    obra_nome: string | null; status: string; prazo_contratual: string | null;
  };
  amostras: AmostraDaGrade[];
  metodos: MetodoDaGrade[];
  baterias: BateriaDaGrade[];
  previsto: { amostra_id: string; metodo_id: string }[];
  programado: {
    id: string; amostra_id: string; metodo_id: string;
    status: string;
    /** Para quando foi programado. Ver `0139`. */
    data_prevista: string | null;
    /** Quando foi FEITO — nunca no futuro. */
    data_execucao: string | null;
    instrucao: string | null;
  }[];
  /* Vazio quando a OS não tem escopo — e aí ela NÃO programa (0220, D1). */
  contratado: ContratadoDaGrade[];
  /** As amostras que ainda vão chegar, com o que já foi marcado nelas. */
  previstas?: AmostraPrevistaDaGrade[];
  /** 0220: a OS tem ensaio no escopo? */
  temEscopo?: boolean;
  /** 0220 (D2): nada programado ainda — o escopo muda livre. */
  escopoLivre?: boolean;
}

export const gradeDaOs = (numero: string) =>
  api<GradeDaOs>(`/programacao/os/${encodeURIComponent(numero)}`);

/* ── Salvar a grade inteira (onda 36) ─────────────────────────────────
 *
 * «Caso tenha necessidade de modificar a programação (inclusão, exclusão
 *  de ensaio), deve ser feita com a revisão da OS.» — 30/08/2026.
 *
 * O corpo é a programação COMPLETA depois do gesto, e não o que mudou. O
 * servidor faz a diferença: célula que sumiu da lista é ensaio cancelado.
 * É o que faz «Salvar» significar o que a tela mostra.
 */

export interface SituacaoDaProgramacao {
  os_id: string;
  revisao: number;
  programacao_revisao: number | null;
  aberta: boolean;
  nunca_programada: boolean;
  ensaios: number;
  diz: string;
}

export interface ResultadoDaGrade {
  criados: number;
  cancelados: number;
  mantidos: number;
  revisao: number;
  detalhe: {
    cancelados: { ensaio_id: string; amostra_codigo: string; metodo_codigo: string }[];
  };
  aviso: string | null;
}

export const salvarGrade = (
  numero: string,
  corpo: {
    celulas: { amostraId: string; metodoId: string }[];
    previstas?: { previstaId: string; metodoId: string }[];
    dataPrevista?: string;
    observacoes?: string;
  },
) => api<ResultadoDaGrade>(
  `/programacao/os/${encodeURIComponent(numero)}`, { metodo: 'POST', corpo });


/* ── O trilho de OS e a instrução de ensaio (onda 46) ─────────────────── */

export interface OsDoTrilho {
  id: string;
  numero: string;
  cliente: string | null;
  obra_nome: string | null;
  prazo_contratual: string | null;
  dias_para_prazo: number | null;
  atrasada: boolean;
  status_tecnico: string;
  amostras_recebidas: number;
  ensaios_total: number;
  escopos: string[];
}

export interface Escopo { codigo: string; nome: string; ordem: number }

export interface ModeloDeInstrucao {
  id: string;
  metodo_id: string;
  metodo_codigo: string;
  escopo: string;
  nome: string;
  texto: string;
  vezes_usado: number;
}

export const trilhoDeOs = (busca?: string) =>
  api<OsDoTrilho[]>(`/programacao/os?busca=${encodeURIComponent(busca ?? '')}`);

export const escoposDeEnsaio = () => api<Escopo[]>('/programacao/escopos');

export const modelosDeInstrucao = (metodoId?: string) =>
  api<ModeloDeInstrucao[]>(
    `/programacao/modelos${metodoId ? `?metodo=${metodoId}` : ''}`);

/**
 * Grava a instrução de UMA lista de ensaios.
 *
 * A lista é o que faz a mesma rota servir à célula e à coluna: um id é a
 * célula; os ids da coluna são a coluna. O `motivo` só é exigido quando
 * algum ensaio já saiu de «programado», e quem cobra é o banco.
 */
export const gravarInstrucao = (corpo: {
  ensaioIds: string[];
  /** 0182: células de amostra que ainda não chegou — vão para `os_ensaio_previsto`. */
  previstos?: { previstaId: string; metodoId: string }[];
  instrucao?: string; motivo?: string;
}) => api<{ gravados: number; ensaios?: number; previstos?: number }>(
  '/programacao/instrucao', { metodo: 'PUT', corpo });

/**
 * Reprogramar — a data PREVISTA de uma lista de ensaios.
 *
 * O §8 pede «reprogramar sem apagar a programação original» e «registrar
 * o motivo dos atrasos». O motivo vai para a trilha de auditoria, que é
 * onde a data anterior continua legível — e é de lá que sai a resposta
 * para «por que esta OS atrasou?» seis meses depois.
 */
export const reprogramar = (corpo: {
  ensaioIds: string[]; dataPrevista: string; motivo: string;
}) => api<{ reprogramados: number }>('/programacao/reprogramar',
  { metodo: 'POST', corpo });

export const salvarModeloDeInstrucao = (corpo: {
  metodoId: string; nome: string; texto: string;
}) => api<ModeloDeInstrucao>('/programacao/modelos',
  { metodo: 'POST', corpo });

export const contarUsoDoModelo = (id: string) =>
  api<{ ok: boolean }>(`/programacao/modelos/${id}/usar`, { metodo: 'POST' });
